/*
  ============================================================================
  bb-garage.js  —  BlackBoost Garage core
  ----------------------------------------------------------------------------
  Owner:    Linea Group (lineagroup.ae) for BlackBoost USA
  Store:    blackboost-usa.myshopify.com  —  theme "Linea DEV - do not publish"
  Created:  2026-08-12

  WHAT THIS IS
  The single source of truth for the shopper's selected vehicle. Every other
  BlackBoost component (fitment badge on the PDP, collection filter, garage bar,
  vehicle hubs) binds to this file and must not re-implement any of the logic.

  PUBLIC CONTRACT — FROZEN. Do not change these signatures.
    window.BBGarage.get()                 -> platform object | null
    window.BBGarage.set(platformId)       -> persists + broadcasts
    window.BBGarage.clear()
    window.BBGarage.subscribe(fn)         -> fn(platform|null), fires immediately,
                                             returns an unsubscribe function
    window.BBGarage.data                  -> the parsed dataset (null until loaded)
    window.BBGarage.fits(productHandle)   -> 'green' | 'red' | 'none'
    window.BBGarage.productsFor(id)       -> array of product handles

  VERDICTS — READ THIS BEFORE RENDERING ANYTHING
  fits() returns the LOGICAL verdict. It is not what you paint on screen.
  The fitment dataset was parsed out of free-text product descriptions and the
  client has not signed it off, so dataset.verified is false. While it is false a
  logical 'green' must be painted AMBER ("we are confirming this exact fitment"),
  never green. A wrong green on a $2,000 part costs the shopper a 20% restocking
  fee plus return freight.
  Use BBGarage.verdict(handle) instead of fits() when you are rendering. It does
  the gating for you and returns display-ready copy. Green switches on by itself
  the day the client flips "verified": true in assets/bb-fitment.json.

  RENDER WITHOUT WRITING LOGIC
  Put the attribute on any element and this file fills it in and keeps it in sync:
    <div data-bb-fit="{{ product.handle }}"></div>                  full block
    <div data-bb-fit="{{ product.handle }}" data-bb-fit-variant="compact"></div>
    <div data-bb-fit="{{ product.handle }}" data-bb-fit-variant="pill"></div>
  Other hooks:
    [data-bb-open-picker]   any button — opens the vehicle picker
    [data-bb-clear]         any button — clears the vehicle
    [data-bb-vehicle-name]  filled with the current vehicle display name
  The host element also gets data-bb-fit-state="amber|green|red|neutral|unset|idle"
  so CSS elsewhere can react (dim a card, hide a row) with no extra JS.
  Add data-bb-fit-manual to opt an element out of auto-rendering.

  The host element MUST be empty. Its contents are replaced on every vehicle
  change. Never put the hook on anything that wraps product.description or any
  other server-rendered copy: that text is most of the crawlable content on a
  product page and this would delete it from the DOM.

  SEO
  The ?bb_v= URL param is a convenience for shareable/pre-filtered views only. It
  is written with history.replaceState and NOTHING in this file touches
  <link rel="canonical">. Do not add that. Ever.

  NO JS
  Nothing required to read or buy a product lives here. With JS off, the bar and
  the picker never appear at all rather than appearing broken.

  Dependencies: none. No jQuery, no CDN, no build step.
  ============================================================================
*/

(function () {
  'use strict';

  if (window.BBGarage && window.BBGarage.__isBBGarage) return;

  /* ------------------------------------------------------------------ config */

  var VERSION = '1.0.0';
  var STORAGE_KEY = 'bb_vehicle'; // canonical: platform id only
  var CACHE_KEY = 'bb_vehicle_cache'; // paint cache only, never authoritative
  var URL_PARAM = 'bb_v';
  var EVENT_NAME = 'bb:vehicle-change';
  var ID_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

  var scriptEl = document.currentScript || document.querySelector('script[data-bb-fitment-url]');
  var settings = window.BBGarageConfig || {};

  function attr(name, fallback) {
    if (scriptEl && scriptEl.getAttribute(name)) return scriptEl.getAttribute(name);
    return fallback;
  }

  var config = {
    dataUrl: settings.dataUrl || attr('data-bb-fitment-url', ''),
    overridesUrl: settings.overridesUrl || attr('data-bb-overrides-url', ''),
    contactUrl: settings.contactUrl || attr('data-bb-contact-url', '/pages/contact'),
    browseUrl: settings.browseUrl || attr('data-bb-browse-url', '/collections/all')
  };

  // Every string the shopper reads. Override with window.BBGarageConfig.copy
  // before this file loads if the client wants different wording.
  var COPY = {
    // Accorciata il 28 agosto 2026. Prima erano tre frasi e il blocco spingeva il
    // nome del prodotto sotto la piega: il verdetto deve stare in tre righe, e
    // chi vuole i dettagli scrive. Il "listed" contro "confirmed" resta esplicito,
    // perche' e' l'unica cosa che ci protegge da un reso su un pezzo da 1.800 dollari.
    amberPill: 'Check fitment',
    amberBody: 'Listed for your {vehicle}. We are confirming this exact fitment.',
    amberAction: 'Ask us before you order',

    greenPill: 'Confirmed fit',
    greenBody: 'Confirmed fitment for your {vehicle}.',
    greenAction: '',

    redPill: 'Does not fit',
    redBody: 'This does not fit your selected vehicle, the {vehicle}.',
    redAction: 'See parts that fit your car',

    neutralPill: 'Not vehicle specific',
    neutralBody: 'Not listed against a specific vehicle, so there is nothing to check against your {vehicle}.',
    neutralBodyNoVehicle: 'This part is not listed against a specific vehicle.',
    neutralAction: 'Ask us about this part',
    // Non e' la stessa cosa di "non specifico per veicolo": qui la compatibilita'
    // esiste, semplicemente non ce l'abbiamo scritta. Dirlo e' piu' onesto che
    // far finta che vada bene su tutto.
    unlistedPill: 'Fit not listed',
    unlistedBody: 'We have not listed this part against a vehicle yet, so we cannot confirm it for your {vehicle}. Ask us and we will check.',
    unlistedBodyNoVehicle: 'We have not listed this part against a vehicle yet.',

    unsetPill: 'Fitment',
    unsetBody: 'Pick your car to check this part.',
    unsetAction: 'Select your vehicle',

    pickerTitle: 'Select your vehicle',
    stepMake: 'Choose a make',
    stepModel: 'Choose a model',
    stepChassis: 'Choose a chassis',
    stepEngine: 'Choose engine and year',
    noChassis: 'Not chassis specific',
    filterPlaceholder: 'Type to filter',
    noMatches: 'Nothing in the list matches that.',
    loading: 'Loading the vehicle list',
    loadError:
      'The vehicle list could not be loaded. Refresh the page, or message us and ' +
      'we will confirm fitment by hand.'
  };
  if (settings.copy) {
    for (var ck in settings.copy) {
      if (Object.prototype.hasOwnProperty.call(settings.copy, ck)) COPY[ck] = settings.copy[ck];
    }
  }

  /* ------------------------------------------------------------ small helpers */

  function noop() {}

  function isEl(node) {
    return node && node.nodeType === 1;
  }

  function closest(node, selector) {
    var el = isEl(node) ? node : node && node.parentElement;
    while (el) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function empty(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function fill(template, vehicle) {
    var name = vehicle && vehicle.display ? vehicle.display : 'vehicle';
    return String(template).replace(/\{vehicle\}/g, name);
  }

  function naturalSort(a, b) {
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  // Accepts a handle, a /products/handle path or a full URL. Always returns a handle.
  function normalizeHandle(value) {
    if (!value) return '';
    var handle = String(value).trim().toLowerCase();
    handle = handle.split('?')[0].split('#')[0];
    if (handle.indexOf('/') !== -1) {
      var parts = handle.split('/').filter(Boolean);
      handle = parts.length ? parts[parts.length - 1] : '';
    }
    return handle;
  }

  function validId(id) {
    return typeof id === 'string' && ID_PATTERN.test(id) ? id : null;
  }

  /* ------------------------------------------------------------------ storage */

  // localStorage throws in Safari private mode and when cookies are blocked.
  // Never let that break the page: fall back to memory for the session.
  var memoryStore = {};

  function readStore(key) {
    try {
      var value = window.localStorage.getItem(key);
      if (value !== null) return value;
    } catch (e) {
      /* ignore */
    }
    return Object.prototype.hasOwnProperty.call(memoryStore, key) ? memoryStore[key] : null;
  }

  function writeStore(key, value) {
    if (value === null) delete memoryStore[key];
    else memoryStore[key] = value;
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  }

  /* ---------------------------------------------------------------- URL param */

  function readUrlId() {
    var match = window.location.search.match(/[?&]bb_v=([^&#]*)/);
    if (!match) return null;
    var raw = '';
    try {
      raw = decodeURIComponent(match[1]);
    } catch (e) {
      raw = match[1];
    }
    return validId(raw);
  }

  // Writes ?bb_v= on the current URL so the view is shareable and ads can land
  // pre-filtered. replaceState only, so no history spam. Canonical is untouched.
  function writeUrlId(id) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (id) url.searchParams.set(URL_PARAM, id);
      else url.searchParams.delete(URL_PARAM);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------------ dataset */

  var dataset = null;
  var loadPromise = null;
  var platformIndex = null; // id -> platform
  var productIndex = null; // handle -> [platform ids]
  var reverseIndex = null; // platform id -> [handles]   (built on demand)
  var cascadeIndex = null; // pruned make > model > chassis > [platform ids]
  var universalIndex = null; // handle -> true, i pezzi che vanno bene su tutto

  /*
    Every lookup table below is keyed by a string that came from the dataset or,
    worse, from ?bb_v= in the URL. A plain {} inherits Object.prototype, so
    platformIndex['__proto__'] and platformIndex['constructor'] come back
    TRUTHY and are not platforms. That made set('__proto__') "succeed": the
    shopper got a vehicle that is not a car, and every product with fitment rows
    then rendered RED, a false "does not fit" on the whole catalogue from a URL
    anyone could share. productsFor('constructor') threw for the same reason.
    Null-prototype objects are the structural fix: no inherited keys, and an
    incoming '__proto__' key becomes an ordinary own property instead of
    reassigning a prototype. Do not turn these back into {}.
  */
  function bareMap() {
    return Object.create(null);
  }

  function buildIndexes(json) {
    platformIndex = bareMap();
    var platforms = (json && json.platforms) || [];
    for (var i = 0; i < platforms.length; i++) {
      var platform = platforms[i];
      if (platform && platform.id) platformIndex[platform.id] = platform;
    }

    productIndex = bareMap();
    var products = (json && json.products) || {};
    for (var handle in products) {
      if (!Object.prototype.hasOwnProperty.call(products, handle)) continue;
      var ids = products[handle] || [];
      var keep = [];
      for (var j = 0; j < ids.length; j++) {
        if (platformIndex[ids[j]]) keep.push(ids[j]);
      }
      // A handle with an empty list is the same as a handle with no row at all:
      // it renders neutral, never red. That is the intercooler / filter / apparel case.
      if (keep.length) productIndex[normalizeHandle(handle)] = keep;
    }

    // Prune the cascade so no path can dead-end. Anything that does not end in a
    // resolvable platform is dropped here rather than checked at render time.
    cascadeIndex = bareMap();
    // Chi va bene su QUALSIASI auto lo dice il dataset, non lo si deduce.
    // Prima, un prodotto senza riga di compatibilita' veniva trattato come
    // universale, e siccome due aspirazioni BMW erano rimaste senza riga
    // comparivano anche sulla scheda di una Mercedes. Adesso "nessuna riga"
    // significa "non lo sappiamo", che e' una cosa diversa da "va bene su tutto".
    universalIndex = bareMap();
    var uni = (json && json.universal) || [];
    for (var u = 0; u < uni.length; u++) universalIndex[normalizeHandle(uni[u])] = true;

    var cascade = (json && json.cascade) || {};
    for (var make in cascade) {
      if (!Object.prototype.hasOwnProperty.call(cascade, make)) continue;
      var models = cascade[make] || {};
      var keptModels = bareMap();
      for (var model in models) {
        if (!Object.prototype.hasOwnProperty.call(models, model)) continue;
        var chassisMap = models[model] || {};
        var keptChassis = bareMap();
        for (var chassis in chassisMap) {
          if (!Object.prototype.hasOwnProperty.call(chassisMap, chassis)) continue;
          var leaf = chassisMap[chassis] || [];
          var live = [];
          for (var k = 0; k < leaf.length; k++) {
            if (platformIndex[leaf[k]]) live.push(leaf[k]);
          }
          if (live.length) keptChassis[chassis] = live;
        }
        if (Object.keys(keptChassis).length) keptModels[model] = keptChassis;
      }
      if (Object.keys(keptModels).length) cascadeIndex[make] = keptModels;
    }

    reverseIndex = null;
  }

  function load() {
    if (loadPromise) return loadPromise;

    if (!config.dataUrl) {
      loadPromise = Promise.reject(new Error('bb-garage: no fitment dataset URL configured'));
      loadPromise.catch(noop);
      announceLoadFailure();
      return loadPromise;
    }

    // Il file delle correzioni e' facoltativo. Se manca, se e' scritto male o se
    // non arriva, il sito va avanti con la base: una modifica a mano non deve
    // poter spegnere la compatibilita' di tutto il negozio.
    var overridesUrl = config.overridesUrl;
    var overridesPromise = overridesUrl
      ? fetch(overridesUrl, { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
      : Promise.resolve(null);

    loadPromise = Promise.all([
      fetch(config.dataUrl, { credentials: 'omit' }).then(function (response) {
        if (!response.ok) throw new Error('bb-garage: fitment dataset HTTP ' + response.status);
        return response.json();
      }),
      overridesPromise
    ])
      .then(function (pair) {
        var json = pair[0];
        try {
          applyOverrides(json, pair[1]);
        } catch (e) {
          if (window.console) console.warn('bb-garage: correzioni ignorate,', e && e.message);
        }
        return json;
      })
      .then(function (json) {
        dataset = json || null;
        buildIndexes(dataset);
        reflectDataset();
        resolveStoredId();
        emit();
        return dataset;
      });

    loadPromise.catch(function () {
      // Dataset down. Everything reports 'none' and renders the neutral line,
      // which is the safe direction: no false red, no false green.
      announceLoadFailure();
    });

    return loadPromise;
  }


  /* ------------------------------------------------- correzioni fatte a mano */

  /*
    bb-fitment-overrides.json viene applicato SOPRA la base.

    PERCHE' UN FILE A PARTE
    La base e' grossa e nasce da un lavoro di incrocio fra il nostro catalogo e
    quello del produttore. Le correzioni sono poche, si fanno a mano e devono
    restare visibili: in un file piccolo si legge cosa e' stato cambiato, si
    capisce perche', e si torna indietro cancellando una riga. Dentro la base
    sparirebbero, e al primo rigenero sarebbero perse.

    Il file si modifica dall'admin di Shopify, in Edit code, dentro Assets.
    Shopify tiene lo storico, quindi un errore si annulla da li'.

    Le auto si indicano col NOME che si legge nel menu, non con un codice: un
    codice non lo sa leggere nessuno e non si controlla a occhio.

    'togli' viene applicato DOPO 'aggiungi', quindi vince sempre. E' voluto: un
    pezzo non trovato e' una vendita persa, un pezzo sull'auto sbagliata e' un
    reso dagli Stati Uniti.
  */
  function applyOverrides(json, ov) {
    if (!json || !ov || typeof ov !== 'object') return;
    var warn = function (m) { if (window.console) console.warn('bb-fitment-overrides: ' + m); };

    json.platforms = json.platforms || [];
    json.products = json.products || {};

    // auto nuove, prima di tutto: le righe sotto possono riferirsi a loro
    var nuove = ov.auto_nuove || [];
    for (var n = 0; n < nuove.length; n++) {
      var a = nuove[n];
      if (!a || !a.display) { warn('auto nuova senza nome, saltata'); continue; }
      var gia = false;
      for (var g = 0; g < json.platforms.length; g++) {
        if (json.platforms[g].display === a.display) { gia = true; break; }
      }
      if (gia) continue;
      json.platforms.push({
        id: 'ov' + n + '-' + String(a.display).replace(/[^A-Za-z0-9]+/g, '').slice(0, 12).toLowerCase(),
        make: a.make || '', model: a.model || '',
        chassis: a.chassis || [], engine: a.engine || '', display: a.display
      });
    }

    var perNome = {};
    for (var p = 0; p < json.platforms.length; p++) {
      if (!(json.platforms[p].display in perNome)) perNome[json.platforms[p].display] = json.platforms[p].id;
    }

    function idsDi(nomi, dove) {
      var out = [];
      for (var i = 0; i < (nomi || []).length; i++) {
        var id = perNome[nomi[i]];
        if (id) out.push(id);
        else warn('in "' + dove + '" l\'auto "' + nomi[i] + '" non esiste, riga ignorata');
      }
      return out;
    }

    var h, ids, k;
    var add = ov.aggiungi || {};
    for (h in add) {
      if (!Object.prototype.hasOwnProperty.call(add, h)) continue;
      ids = idsDi(add[h], h);
      if (!ids.length) continue;
      var cur = json.products[h] || [];
      for (k = 0; k < ids.length; k++) if (cur.indexOf(ids[k]) === -1) cur.push(ids[k]);
      json.products[h] = cur;
    }

    var rem = ov.togli || {};
    for (h in rem) {
      if (!Object.prototype.hasOwnProperty.call(rem, h)) continue;
      if (!json.products[h]) continue;
      ids = idsDi(rem[h], h);
      json.products[h] = json.products[h].filter(function (x) { return ids.indexOf(x) === -1; });
      if (!json.products[h].length) delete json.products[h];
    }

    var uni = ov.universale || [];
    if (uni.length) {
      json.universal = (json.universal || []).slice();
      for (k = 0; k < uni.length; k++) {
        if (json.universal.indexOf(uni[k]) === -1) json.universal.push(uni[k]);
        delete json.products[uni[k]];
      }
    }

    // il cascade si ricostruisce, altrimenti le auto nuove non compaiono nel menu
    var byId = {};
    for (k = 0; k < json.platforms.length; k++) byId[json.platforms[k].id] = json.platforms[k];
    var usate = {};
    for (h in json.products) {
      if (!Object.prototype.hasOwnProperty.call(json.products, h)) continue;
      for (k = 0; k < json.products[h].length; k++) usate[json.products[h][k]] = true;
    }
    var casc = {};
    for (var u in usate) {
      if (!Object.prototype.hasOwnProperty.call(usate, u)) continue;
      var pl = byId[u];
      if (!pl) continue;
      var ch = (pl.chassis && pl.chassis.length) ? pl.chassis.join('/') : '-';
      if (!casc[pl.make]) casc[pl.make] = {};
      if (!casc[pl.make][pl.model]) casc[pl.make][pl.model] = {};
      if (!casc[pl.make][pl.model][ch]) casc[pl.make][pl.model][ch] = [];
      if (casc[pl.make][pl.model][ch].indexOf(u) === -1) casc[pl.make][pl.model][ch].push(u);
    }
    json.cascade = casc;
  }

  function announceLoadFailure() {
    document.documentElement.setAttribute('data-bb-fitment', 'unavailable');
    document.dispatchEvent(new CustomEvent('bb:fitment-error', { bubbles: true }));
  }

  function reflectDataset() {
    var root = document.documentElement;
    root.setAttribute('data-bb-fitment', 'ready');
    // Green is gated on this. It is false today and the client flips it in the JSON.
    root.setAttribute('data-bb-verified', dataset && dataset.verified === true ? 'true' : 'false');
  }

  /* -------------------------------------------------------------------- state */

  // state.platform is either a real dataset platform or null. It is never a
  // partial object: components read .make / .chassis / .engine off it and a stub
  // would quietly break them. The pre-dataset display string lives separately in
  // state.label and is used for painting only.
  var state = { id: null, platform: null, label: '' };
  var subscribers = [];

  function storedId() {
    // Da dove arriva l'id conta: vedi resolveStoredId.
    var fromUrl = readUrlId();
    if (fromUrl) {
      state.idFromUrl = fromUrl;
      return fromUrl;
    }
    state.idFromUrl = null;
    return validId(readStore(STORAGE_KEY));
  }

  function cachedDisplay(id) {
    try {
      var cache = JSON.parse(readStore(CACHE_KEY) || 'null');
      if (cache && cache.id === id && cache.display) return String(cache.display);
    } catch (e) {
      /* ignore */
    }
    return '';
  }

  // Belt and braces on top of the null-prototype maps. A "platform" is only a
  // platform if it carries back the id we looked it up with. Anything else
  // becomes a vehicle that is not a car, and a vehicle that is not a car turns
  // every product with fitment rows RED. Cheap check, worst failure in the system.
  function realPlatform(id, candidate) {
    return candidate && typeof candidate === 'object' && candidate.id === id ? candidate : null;
  }

  function resolveStoredId() {
    if (!state.id) {
      state.platform = null;
      state.label = '';
      return;
    }
    var platform = platformIndex ? realPlatform(state.id, platformIndex[state.id]) : null;
    if (platform) {
      state.platform = platform;
      state.label = platform.display || platform.id;
      writeStore(CACHE_KEY, JSON.stringify({ id: platform.id, display: platform.display || '' }));
      // 1 ott 2026: l'auto arrivata con ?bb_v= (annuncio, link condiviso) restava
      // solo in memoria, e al primo clic su una pagina senza ?bb_v= il garage
      // tornava vuoto. Si salva qui, solo ora che il dataset conferma che
      // l'auto esiste: un link scaduto non arriva mai a questo punto (vedi sotto).
      if (state.idFromUrl) {
        writeStore(STORAGE_KEY, platform.id);
        state.idFromUrl = null;
      }
    } else if (platformIndex) {
      // L'id non esiste piu' nel dataset: l'auto e' stata tolta dal catalogo.
      //
      // Da dove arrivava fa la differenza. Se veniva da un ?bb_v= in un link
      // vecchio (un annuncio, una mail, un messaggio di mesi fa), NON si tocca
      // l'auto che il cliente aveva gia' scelto: si ignora il parametro e si
      // ripiega su quella salvata. Prima un link scaduto svuotava il garage e
      // costringeva a rifare tutta la scelta, per colpa di un link nostro.
      var salvato = validId(readStore(STORAGE_KEY));
      if (state.idFromUrl && salvato && salvato !== state.id) {
        state.id = salvato;
        state.idFromUrl = null;
        writeUrlId(null);
        resolveStoredId();
        return;
      }
      state.id = null;
      state.platform = null;
      state.label = '';
      writeStore(STORAGE_KEY, null);
      writeStore(CACHE_KEY, null);
      writeUrlId(null);
    }
  }

  function emit() {
    var value = state.platform;

    // Reflected off state.id, which is known synchronously, so CSS can react on
    // the first paint instead of waiting for the dataset fetch.
    var root = document.documentElement;
    if (state.id) {
      root.setAttribute('data-bb-vehicle', state.id);
      root.setAttribute('data-bb-has-vehicle', 'true');
    } else {
      root.removeAttribute('data-bb-vehicle');
      root.setAttribute('data-bb-has-vehicle', 'false');
    }

    renderAll();

    for (var i = 0; i < subscribers.length; i++) {
      try {
        subscribers[i](value);
      } catch (e) {
        if (window.console) console.error('bb-garage: subscriber failed', e);
      }
    }

    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value, bubbles: true }));
  }

  /* ------------------------------------------------------------------ verdict */

  // A handle's rows are only rows if they are a real array. Anything else is
  // treated as "no fitment data", which renders neutral. Never red, never a throw.
  function rowsFor(handle) {
    if (!productIndex) return null;
    var ids = productIndex[normalizeHandle(handle)];
    return Array.isArray(ids) && ids.length ? ids : null;
  }

  function fits(handle) {
    if (!productIndex) return 'none'; // dataset not loaded: neutral, never red
    var ids = rowsFor(handle);
    if (!ids) {
      // universale = va bene comunque; senza riga = non lo sappiamo
      return (universalIndex && universalIndex[normalizeHandle(handle)]) ? 'none' : 'unlisted';
    }
    if (!state.platform) return 'none'; // no vehicle selected yet
    return ids.indexOf(state.platform.id) !== -1 ? 'green' : 'red';
  }

  // Display-ready verdict. This is what renderers should use.
  // state: 'idle' | 'neutral' | 'unset' | 'amber' | 'green' | 'red'
  function verdict(handle) {
    var vehicle = state.platform;
    var out = { state: 'idle', pill: '', body: '', action: null, vehicle: vehicle, handle: normalizeHandle(handle) };

    if (!productIndex) return out; // still loading, or dataset unavailable

    var ids = rowsFor(out.handle);

    if (!ids) {
      var isUni = universalIndex && universalIndex[out.handle];
      out.state = isUni ? 'neutral' : 'unlisted';
      out.pill = isUni ? COPY.neutralPill : COPY.unlistedPill;
      if (isUni) {
        out.body = vehicle ? fill(COPY.neutralBody, vehicle) : COPY.neutralBodyNoVehicle;
      } else {
        out.body = vehicle ? fill(COPY.unlistedBody, vehicle) : COPY.unlistedBodyNoVehicle;
      }
      if (COPY.neutralAction) out.action = { label: COPY.neutralAction, href: config.contactUrl };
      return out;
    }

    if (!vehicle) {
      out.state = 'unset';
      out.pill = COPY.unsetPill;
      out.body = COPY.unsetBody;
      out.action = { label: COPY.unsetAction, picker: true };
      return out;
    }

    if (ids.indexOf(vehicle.id) !== -1) {
      // Gate. Until the client signs the data off, a match is amber, not green.
      if (dataset && dataset.verified === true) {
        out.state = 'green';
        out.pill = COPY.greenPill;
        out.body = fill(COPY.greenBody, vehicle);
        if (COPY.greenAction) out.action = { label: COPY.greenAction, href: config.contactUrl };
      } else {
        out.state = 'amber';
        out.pill = COPY.amberPill;
        out.body = fill(COPY.amberBody, vehicle);
        out.action = { label: COPY.amberAction, href: config.contactUrl };
      }
      return out;
    }

    out.state = 'red';
    out.pill = COPY.redPill;
    out.body = fill(COPY.redBody, vehicle);
    out.action = { label: COPY.redAction, href: browseHref(vehicle.id) };
    return out;
  }

  /*
    Il bottone "See parts for my car" della barra.
    L'indirizzo si scrive qui e non nel liquid perche' dipende dall'auto
    scelta, che il liquid non conosce: la pagina e' la stessa per tutti e
    viene dalla cache di Shopify.
    Il parametro bb_only=1 dice alla pagina collection di partire con il
    filtro "solo i pezzi per la mia auto" gia' acceso: senza, si arrivava
    su tutto il catalogo e il filtro andava cercato a mano.
  */
  function paintShopLink(platform) {
    var links = document.querySelectorAll('[data-bb-shop-link]');
    for (var i = 0; i < links.length; i++) {
      if (platform && platform.id) {
        // Dal 3 set 2026 i prodotti portano il tag fit-<id> dell'auto (messo sul
        // negozio vivo con tagsAdd): la pagina-tag di Shopify filtra lato server,
        // su TUTTO il catalogo, e non solo sui 36 della prima pagina come faceva
        // il filtro nel browser. bb_v resta perche' la barra di pagina lo legge.
        links[i].setAttribute('href', tagHref(platform.id));
        links[i].hidden = false;
      } else {
        links[i].hidden = true;
      }
    }
  }

  function tagHref(id) {
    var base = (config.browseUrl || '/collections/all').split('?')[0].replace(/\/$/, '');
    return base + '/fit-' + encodeURIComponent(id) + '?' + URL_PARAM + '=' + encodeURIComponent(id);
  }

  /*
    Scelta l'auto, si va dritti ai suoi pezzi (Sebastiano, 4 set 2026): la
    pagina-tag /collections/all/fit-<id>, la stessa del bottone della barra.
    Non dalla scheda prodotto: li' il cliente sta guardando un pezzo preciso e
    la pagina gli dice subito se va bene sulla sua auto.
  */
  function vaiAiPezzi(id) {
    if (!id) return;
    if (location.pathname.indexOf('/products/') === 0) return;
    var dest = tagHref(id);
    if (location.pathname === dest.split('?')[0]) return;
    location.assign(dest);
  }

  /*
    L'indirizzo dei pezzi di UN'AUTO. Dal 7 set 2026 questa e' l'unica forma:
    /collections/all/fit-<id>, la pagina-tag che Shopify filtra lato server.
    Prima tornava /collections/all?bb_v=<id>, cioe' tutto il catalogo con il
    filtro nel browser: chi ci arrivava dalla scheda prodotto vedeva 134 prodotti
    con l'etichetta rossa "DOES NOT FIT" addosso. Ogni parte del sito che manda
    "ai pezzi per la mia auto" passa di qui, cosi' non puo' piu' divergere.
  */
  function browseHref(id) {
    return tagHref(id);
  }

  /* Il catalogo intero, senza filtro: serve solo quando per quell'auto non
     abbiamo NIENTE, e mandarlo su una pagina vuota sarebbe peggio. */
  function catalogHref() {
    return config.browseUrl || '/collections/all';
  }

  function productsFor(platformId) {
    if (!productIndex) return [];
    if (!reverseIndex) {
      reverseIndex = bareMap();
      for (var handle in productIndex) {
        if (!Object.prototype.hasOwnProperty.call(productIndex, handle)) continue;
        var ids = productIndex[handle];
        for (var i = 0; i < ids.length; i++) {
          if (!reverseIndex[ids[i]]) reverseIndex[ids[i]] = [];
          reverseIndex[ids[i]].push(handle);
        }
      }
    }
    var found = reverseIndex[platformId];
    return Array.isArray(found) ? found.slice() : [];
  }

  /* -------------------------------------------------------- cascade accessors */

  function makes() {
    if (!cascadeIndex) return [];
    return Object.keys(cascadeIndex).sort(naturalSort);
  }

  function models(make) {
    if (!cascadeIndex || !cascadeIndex[make]) return [];
    return Object.keys(cascadeIndex[make]).sort(naturalSort);
  }

  // Returns [{ key, label, ids }]. key '-' means the source data had no chassis code.
  function chassisOptions(make, model) {
    if (!cascadeIndex || !cascadeIndex[make] || !cascadeIndex[make][model]) return [];
    var node = cascadeIndex[make][model];
    return Object.keys(node)
      .sort(naturalSort)
      .map(function (key) {
        return {
          key: key,
          label: key === '-' ? COPY.noChassis : key.replace(/\//g, ' / '),
          ids: node[key].slice()
        };
      });
  }

  function platformsFor(make, model, chassisKey) {
    var options = chassisOptions(make, model);
    var ids = [];
    for (var i = 0; i < options.length; i++) {
      if (chassisKey == null || options[i].key === chassisKey) ids = ids.concat(options[i].ids);
    }
    var out = [];
    for (var j = 0; j < ids.length; j++) {
      if (platformIndex[ids[j]]) out.push(platformIndex[ids[j]]);
    }
    return out;
  }

  function yearLabel(platform) {
    var from = platform.year_from;
    var to = platform.year_to;
    if (!from && !to) return '';
    if (from && !to) return from + '+';
    if (!from && to) return 'up to ' + to;
    if (from === to) return String(from);
    return from + '-' + to;
  }

  // Engine step label. 50 platforms have no engine code and 40 have no
  // displacement, so fall back through years and then the raw display string.
  function engineLabel(platform) {
    var bits = [];
    if (platform.engine) bits.push(platform.engine);
    if (platform.displacement) bits.push(platform.displacement);
    if (bits.length) return bits.join(' ');
    var years = yearLabel(platform);
    if (years) return years;
    return platform.display || platform.id;
  }

  /* ------------------------------------------------------------- render hooks */

  var FIT_SELECTOR = '[data-bb-fit]:not([data-bb-fit-manual])';
  var NAME_SELECTOR = '[data-bb-vehicle-name]';

  function renderFit(host) {
    var result = verdict(host.getAttribute('data-bb-fit'));
    var variant = host.getAttribute('data-bb-fit-variant') || 'full';

    host.setAttribute('data-bb-fit-state', result.state);

    if (result.state === 'idle') {
      empty(host);
      return;
    }

    var block = el('div', 'bb-verdict bb-verdict--' + result.state + ' bb-verdict--' + variant);
    if (variant === 'full') {
      block.setAttribute('role', 'status');
      block.setAttribute('aria-live', 'polite');
    }

    var pill = el('span', 'bb-pill bb-pill--' + result.state, result.pill);
    block.appendChild(pill);

    if (variant !== 'pill') {
      var body = el('p', 'bb-verdict__body', result.body);
      block.appendChild(body);

      if (result.action && variant === 'full') {
        var action;
        if (result.action.picker) {
          action = el('button', 'bb-verdict__action bb-btn bb-btn--ghost', result.action.label);
          action.type = 'button';
          action.setAttribute('data-bb-open-picker', '');
        } else {
          action = el('a', 'bb-verdict__action bb-btn bb-btn--ghost', result.action.label);
          action.href = result.action.href;
        }
        block.appendChild(action);
      }
    } else if (result.body) {
      pill.title = result.body;
    }

    empty(host);
    host.appendChild(block);
  }

  function currentLabel() {
    if (state.platform) return state.platform.display || state.platform.id;
    // Dataset not in yet: paint the cached name so a returning shopper never sees
    // "What do you drive?" flash before their car appears.
    return state.id ? state.label : '';
  }

  function renderNames(root) {
    var nodes = (root || document).querySelectorAll(NAME_SELECTOR);
    var label = currentLabel();
    for (var i = 0; i < nodes.length; i++) {
      var text = label || nodes[i].getAttribute('data-bb-empty-text') || '';
      nodes[i].textContent = text;
      if (text) nodes[i].title = text;
      else nodes[i].removeAttribute('title');
    }
  }

  function renderBars(root) {
    var bars = (root || document).querySelectorAll('[data-bb-bar]');
    var isSet = !!currentLabel();
    for (var i = 0; i < bars.length; i++) {
      bars[i].setAttribute('data-bb-state', isSet ? 'set' : 'empty');
    }
  }

  function renderAll(root) {
    paintShopLink(state.platform);
    var scope = root || document;
    var hooks = scope.querySelectorAll(FIT_SELECTOR);
    for (var i = 0; i < hooks.length; i++) renderFit(hooks[i]);
    renderNames(scope);
    renderBars(scope);
  }

  // Collection grids and quick-add drawers inject markup after load. Pick those up.
  function watchDom() {
    if (!window.MutationObserver) return;
    var queued = [];
    var scheduled = false;

    function flush() {
      scheduled = false;
      var nodes = queued;
      queued = [];
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (!node.isConnected) continue;
        if (node.matches && node.matches(FIT_SELECTOR)) renderFit(node);
        renderAll(node);
      }
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (!isEl(added[j])) continue;
          if (
            (added[j].matches && added[j].matches(FIT_SELECTOR + ',' + NAME_SELECTOR + ',[data-bb-bar]')) ||
            (added[j].querySelector &&
              added[j].querySelector(FIT_SELECTOR + ',' + NAME_SELECTOR + ',[data-bb-bar]'))
          ) {
            queued.push(added[j]);
          }
        }
      }
      if (queued.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------- the picker */

  var picker = {
    root: null,
    panel: null,
    optionsEl: null,
    promptEl: null,
    crumbsEl: null,
    filterWrap: null,
    filterEl: null,
    emptyEl: null,
    statusEl: null,
    liveEl: null,
    backEl: null,
    usesDialog: false,
    lastFocus: null,
    step: 'make',
    choice: { make: null, model: null, chassis: null },
    options: [],
    filterText: ''
  };

  function mountPicker() {
    var nodes = document.querySelectorAll('[data-bb-modal]');
    if (!nodes.length) return null;
    // Singleton. If a template rendered the snippet twice, keep the first.
    for (var i = 1; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
    if (picker.root === nodes[0]) return picker.root;

    picker.root = nodes[0];
    picker.panel = picker.root.querySelector('[data-bb-panel]');
    picker.optionsEl = picker.root.querySelector('[data-bb-options]');
    picker.promptEl = picker.root.querySelector('[data-bb-prompt]');
    picker.crumbsEl = picker.root.querySelector('[data-bb-crumbs]');
    picker.filterWrap = picker.root.querySelector('[data-bb-filter-wrap]');
    picker.filterEl = picker.root.querySelector('[data-bb-filter]');
    picker.emptyEl = picker.root.querySelector('[data-bb-empty]');
    picker.statusEl = picker.root.querySelector('[data-bb-status]');
    picker.liveEl = picker.root.querySelector('[data-bb-live]');
    picker.backEl = picker.root.querySelector('[data-bb-back]');
    picker.usesDialog =
      typeof picker.root.showModal === 'function' && typeof HTMLDialogElement !== 'undefined';

    if (!picker.usesDialog) picker.root.classList.add('bb-modal--fallback');

    picker.root.addEventListener('click', function (event) {
      // Click on the backdrop area (the dialog element itself) closes.
      if (event.target === picker.root) closePicker();
      if (closest(event.target, '[data-bb-close]')) {
        event.preventDefault();
        closePicker();
      }
      var option = closest(event.target, '[data-bb-option]');
      if (option) {
        event.preventDefault();
        chooseOption(option.getAttribute('data-bb-option'));
      }
      if (closest(event.target, '[data-bb-back]')) {
        event.preventDefault();
        stepBack();
      }
    });

    picker.root.addEventListener('cancel', function (event) {
      event.preventDefault(); // run our own close so state resets cleanly
      closePicker();
    });

    picker.root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closePicker();
        return;
      }
      if (event.key === 'Tab') trapTab(event);
      else handleArrowKeys(event);
    });

    if (picker.filterEl) {
      picker.filterEl.addEventListener('input', function () {
        picker.filterText = picker.filterEl.value.trim().toLowerCase();
        paintOptions();
      });
      picker.filterEl.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          var first = picker.optionsEl.querySelector('[data-bb-option]:not([hidden])');
          if (first) chooseOption(first.getAttribute('data-bb-option'));
        }
      });
    }

    return picker.root;
  }

  function focusables() {
    if (!picker.root) return [];
    var nodes = picker.root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) out.push(nodes[i]);
    }
    return out;
  }

  function trapTab(event) {
    var list = focusables();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleArrowKeys(event) {
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(event.key) === -1) return;
    var visible = picker.optionsEl
      ? Array.prototype.slice.call(picker.optionsEl.querySelectorAll('[data-bb-option]:not([hidden])'))
      : [];
    if (!visible.length) return;
    var index = visible.indexOf(document.activeElement);
    var next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = visible.length - 1;
    else if (index === -1) next = 0;
    else next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    if (next < 0) next = visible.length - 1;
    if (next >= visible.length) next = 0;
    event.preventDefault();
    visible[next].focus();
  }

  var scrollLock = { top: 0, pad: '' };

  function lockScroll() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    scrollLock.pad = document.body.style.paddingRight;
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
    document.documentElement.classList.add('bb-modal-open');
  }

  function unlockScroll() {
    document.documentElement.classList.remove('bb-modal-open');
    document.body.style.paddingRight = scrollLock.pad;
  }

  function openPicker() {
    if (!mountPicker()) {
      if (window.console) {
        console.warn('bb-garage: no [data-bb-modal] on this page. Render bb-garage-modal (or bb-garage-bar).');
      }
      return;
    }
    picker.lastFocus = document.activeElement;
    picker.step = 'make';
    picker.choice = { make: null, model: null, chassis: null };
    picker.filterText = '';
    if (picker.filterEl) picker.filterEl.value = '';

    if (picker.usesDialog) picker.root.showModal();
    else picker.root.setAttribute('open', '');
    lockScroll();

    if (dataset) {
      setStatus('');
      renderStep();
      return;
    }

    setStatus(COPY.loading);
    if (picker.optionsEl) empty(picker.optionsEl);
    load().then(
      function () {
        if (!picker.root.hasAttribute('open')) return;
        setStatus('');
        renderStep();
      },
      function () {
        if (!picker.root.hasAttribute('open')) return;
        setStatus(COPY.loadError, true);
        if (picker.optionsEl) empty(picker.optionsEl);
      }
    );
  }

  function closePicker() {
    if (!picker.root) return;
    if (picker.usesDialog && picker.root.open) picker.root.close();
    picker.root.removeAttribute('open');
    unlockScroll();
    if (picker.lastFocus && picker.lastFocus.focus) picker.lastFocus.focus();
    picker.lastFocus = null;
  }

  // Visible message line (loading, load failure).
  function setStatus(message, isError) {
    if (!picker.statusEl) return;
    picker.statusEl.textContent = message || '';
    picker.statusEl.hidden = !message;
    picker.statusEl.classList.toggle('bb-modal__status--error', !!isError);
  }

  // Screen-reader only announcement of the step change.
  function announce(message) {
    if (picker.liveEl) picker.liveEl.textContent = message;
  }

  function stepIndex() {
    return ['make', 'model', 'chassis', 'engine'].indexOf(picker.step);
  }

  // Going back one step also drops the choice that step is about to re-ask for,
  // otherwise the breadcrumb keeps showing an answer the shopper is replacing.
  function stepBack() {
    if (picker.step === 'engine') {
      picker.choice.chassis = null;
      // The chassis step may have been skipped. Go back to whichever was real.
      if (chassisOptions(picker.choice.make, picker.choice.model).length > 1) {
        picker.step = 'chassis';
      } else {
        picker.choice.model = null;
        picker.step = 'model';
      }
    } else if (picker.step === 'chassis') {
      picker.choice.chassis = null;
      picker.choice.model = null;
      picker.step = 'model';
    } else if (picker.step === 'model') {
      picker.choice.model = null;
      picker.choice.make = null;
      picker.step = 'make';
    } else {
      closePicker();
      return;
    }
    picker.filterText = '';
    if (picker.filterEl) picker.filterEl.value = '';
    renderStep();
  }

  function chooseOption(value) {
    if (picker.step === 'make') {
      picker.choice.make = value;
      picker.step = 'model';
    } else if (picker.step === 'model') {
      picker.choice.model = value;
      var options = chassisOptions(picker.choice.make, value);
      if (options.length === 1) {
        // Only one chassis path: never make the shopper click through a
        // single-option screen, and never let a step dead-end.
        picker.choice.chassis = options[0].key;
        picker.step = 'engine';
      } else {
        picker.step = 'chassis';
      }
    } else if (picker.step === 'chassis') {
      picker.choice.chassis = value;
      picker.step = 'engine';
    } else {
      api.set(value);
      closePicker();
      vaiAiPezzi(value);
      return;
    }

    // Auto-advance past an engine step that has a single answer.
    if (picker.step === 'engine') {
      var platforms = platformsFor(picker.choice.make, picker.choice.model, picker.choice.chassis);
      if (platforms.length === 1) {
        api.set(platforms[0].id);
        closePicker();
        vaiAiPezzi(platforms[0].id);
        return;
      }
    }

    picker.filterText = '';
    if (picker.filterEl) picker.filterEl.value = '';
    renderStep();
  }

  function renderStep() {
    if (!picker.root) return;
    setStatus('');

    var prompts = {
      make: COPY.stepMake,
      model: COPY.stepModel,
      chassis: COPY.stepChassis,
      engine: COPY.stepEngine
    };

    if (picker.step === 'make') {
      picker.options = makes().map(function (make) {
        return { value: make, label: make, meta: '' };
      });
    } else if (picker.step === 'model') {
      picker.options = models(picker.choice.make).map(function (model) {
        return { value: model, label: model, meta: '' };
      });
    } else if (picker.step === 'chassis') {
      picker.options = chassisOptions(picker.choice.make, picker.choice.model).map(function (option) {
        return { value: option.key, label: option.label, meta: '' };
      });
    } else {
      picker.options = platformsFor(picker.choice.make, picker.choice.model, picker.choice.chassis).map(
        function (platform) {
          var label = engineLabel(platform);
          var years = yearLabel(platform);
          // 50 rows have no engine code, so engineLabel already fell back to the
          // years. Do not print them twice.
          return { value: platform.id, label: label, meta: years === label ? '' : years };
        }
      );
    }

    if (picker.promptEl) picker.promptEl.textContent = prompts[picker.step];

    if (picker.crumbsEl) {
      var crumbs = [];
      if (picker.choice.make) crumbs.push(picker.choice.make);
      if (picker.choice.model) crumbs.push(picker.choice.model);
      if (picker.choice.chassis && picker.choice.chassis !== '-') crumbs.push(picker.choice.chassis);
      picker.crumbsEl.textContent = crumbs.join('  ›  ');
      picker.crumbsEl.hidden = crumbs.length === 0;
    }

    if (picker.backEl) picker.backEl.hidden = picker.step === 'make';

    var showFilter = picker.options.length > 12;
    if (picker.filterWrap) picker.filterWrap.hidden = !showFilter;

    paintOptions();

    // 1 ott 2026: sui telefoni il fuoco automatico sul filtro apriva la tastiera
    // sopra la lista delle marche, cioe' sopra la cosa da guardare. Con un dito
    // (pointer coarse) il fuoco va al titolo del passo e la tastiera si apre
    // solo se il cliente tocca il campo. Con mouse e tastiera resta com'era.
    if (showFilter && picker.filterEl && !coarsePointer()) picker.filterEl.focus();
    else if (picker.promptEl) picker.promptEl.focus();
  }

  function coarsePointer() {
    try {
      return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    } catch (e) {
      return false;
    }
  }

  function paintOptions() {
    if (!picker.optionsEl) return;
    empty(picker.optionsEl);

    var visible = 0;
    for (var i = 0; i < picker.options.length; i++) {
      var option = picker.options[i];
      var haystack = (option.label + ' ' + option.meta).toLowerCase();
      var match = !picker.filterText || haystack.indexOf(picker.filterText) !== -1;

      var button = el('button', 'bb-option');
      button.type = 'button';
      button.setAttribute('data-bb-option', option.value);
      button.appendChild(el('span', 'bb-option__label', option.label));
      if (option.meta) button.appendChild(el('span', 'bb-option__meta', option.meta));
      if (!match) button.hidden = true;
      else visible++;
      picker.optionsEl.appendChild(button);
    }

    if (picker.emptyEl) picker.emptyEl.hidden = visible !== 0;

    announce(
      'Step ' +
        (stepIndex() + 1) +
        ' of 4. ' +
        (picker.promptEl ? picker.promptEl.textContent + '. ' : '') +
        visible +
        ' options.'
    );
  }

  /* ------------------------------------------------------------ global events */

  document.addEventListener('click', function (event) {
    if (closest(event.target, '[data-bb-open-picker]')) {
      event.preventDefault();
      openPicker();
      return;
    }
    if (closest(event.target, '[data-bb-clear]')) {
      event.preventDefault();
      api.clear();
    }
  });

  // Two tabs, one shopper. Keep the garage in sync.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    state.id = validId(event.newValue);
    resolveStoredId();
    emit();
  });

  /* ---------------------------------------------------------------- public API */

  var api = {
    __isBBGarage: true,
    VERSION: VERSION,
    STORAGE_KEY: STORAGE_KEY,
    URL_PARAM: URL_PARAM,
    EVENT: EVENT_NAME,

    get: function () {
      return state.platform;
    },

    set: function (platformId, options) {
      var id = validId(platformId);
      if (!id) return null;
      var updateUrl = !(options && options.updateUrl === false);

      state.id = id;
      writeStore(STORAGE_KEY, id);
      resolveStoredId();
      if (updateUrl) writeUrlId(state.id);
      emit();

      if (!platformIndex) {
        // Set before the dataset landed. Resolve and re-broadcast when it does.
        load().then(function () {
          resolveStoredId();
          emit();
        }, noop);
      }
      return state.platform;
    },

    clear: function () {
      state.id = null;
      state.platform = null;
      state.label = '';
      writeStore(STORAGE_KEY, null);
      writeStore(CACHE_KEY, null);
      writeUrlId(null);
      emit();
    },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return noop;
      subscribers.push(fn);
      try {
        fn(state.platform); // fires immediately with the current value
      } catch (e) {
        if (window.console) console.error('bb-garage: subscriber failed', e);
      }
      return function unsubscribe() {
        var index = subscribers.indexOf(fn);
        if (index !== -1) subscribers.splice(index, 1);
      };
    },

    fits: fits,
    productsFor: productsFor,

    /* --- beyond the frozen contract, safe to use --- */

    verdict: verdict, // display-ready, does the amber gating for you
    ready: function () {
      return load();
    },
    storedId: function () {
      return state.id;
    },
    cachedDisplay: cachedDisplay,
    platform: function (id) {
      return platformIndex ? realPlatform(id, platformIndex[id]) : null;
    },
    isVerified: function () {
      return !!(dataset && dataset.verified === true);
    },
    makes: makes,
    models: models,
    chassisOptions: chassisOptions,
    platformsFor: platformsFor,
    engineLabel: engineLabel,
    yearLabel: yearLabel,
    browseHref: browseHref,
    tagHref: tagHref,
    catalogHref: catalogHref,
    openPicker: openPicker,
    closePicker: closePicker,
    refresh: renderAll,
    config: config,
    copy: COPY
  };

  Object.defineProperty(api, 'data', {
    get: function () {
      return dataset;
    },
    enumerable: true
  });

  window.BBGarage = api;

  /* ---------------------------------------------------------------- bootstrap */

  function boot() {
    document.documentElement.classList.add('bb-ready');
    state.id = storedId();
    state.label = state.id ? cachedDisplay(state.id) : '';

    mountPicker();
    watchDom();
    emit();

    load().then(noop, noop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
